import type { ClientOnErrorFunction } from 'react-router-dom';
import { useUserStore } from '@/store/useUserStore';
import { describeError } from './routeError';

/**
 * Reports a crash the error page shows to the backend (POST /client-errors).
 *
 * A plain `fetch`, not `api.ts`: that client signs the user out on a 401 and
 * caches, and a crash report must never change anything for the user. Every
 * failure is swallowed, nothing is toasted, and the browser session sends each
 * crash (name + message + path) once and at most MAX_REPORTS_PER_SESSION in
 * all. Only the pathname is sent: query strings and hashes can carry tokens.
 */

export type ClientErrorKind = 'render' | 'chunk';

export interface ClientErrorReport {
  kind: ClientErrorKind;
  message: string;
  name: string;
  stack: string | null;
  component_stack: string | null;
  path: string;
  route: string | null;
  build: string;
  app_env: string | null;
  occurred_at: string;
}

export const MAX_REPORTS_PER_SESSION = 10;
const SENT_KEY = 'client-error-reports';

// keepalive requests may carry 64 KB in total; a report is only useful if it
// arrives, so the long fields are capped well under that.
const MAX_MESSAGE_LENGTH = 2_000;
const MAX_STACK_LENGTH = 16_000;
const MAX_FINGERPRINT_LENGTH = 500;

// Short commit (or version + build time) baked in by vite.config.ts. Not
// defined under vitest, hence the typeof guard.
const BUILD = typeof __APP_BUILD__ === 'string' ? __APP_BUILD__ : 'unknown';

const truncate = (value: string | null, max: number): string | null =>
  value === null || value.length <= max ? value : `${value.slice(0, max)}…`;

/** Pathname only: drops any query string or hash. */
export const stripQueryAndHash = (path: string): string => path.split(/[?#]/)[0] || '/';

// What React Router knows about a render error but the error page does not:
// the matched route pattern and React's component stack. Filled by
// `<RouterProvider onError>` in componentDidCatch, which runs before the error
// page's effect sends the report.
interface RouteErrorContext {
  route: string | null;
  componentStack: string | null;
}
const routeErrorContexts = new WeakMap<object, RouteErrorContext>();

/** `<RouterProvider onError>`: remembers the route pattern and component stack for the report. */
export const rememberRouteError: ClientOnErrorFunction = (error, { pattern, errorInfo }) => {
  if (typeof error !== 'object' || error === null) return;
  routeErrorContexts.set(error, {
    route: pattern || null,
    componentStack: errorInfo?.componentStack || null,
  });
};

export function buildClientErrorReport(
  error: unknown,
  { kind, path }: { kind: ClientErrorKind; path: string },
): ClientErrorReport {
  const { name, message, stack } = describeError(error);
  const context = typeof error === 'object' && error !== null ? routeErrorContexts.get(error) : undefined;
  return {
    kind,
    message: truncate(message, MAX_MESSAGE_LENGTH) ?? '',
    name,
    stack: truncate(stack, MAX_STACK_LENGTH),
    component_stack: truncate(context?.componentStack ?? null, MAX_STACK_LENGTH),
    path: stripQueryAndHash(path),
    route: context?.route ?? null,
    build: BUILD,
    app_env: import.meta.env.VITE_APP_ENV ?? null,
    occurred_at: new Date().toISOString(),
  };
}

// Fingerprints already sent this session live in sessionStorage. When storage
// refuses a write (disabled, full), this page load keeps them here instead, so
// the limits still hold until the next reload.
let unsavedSent: string[] = [];

function readSent(): string[] {
  let saved: string[] = [];
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(SENT_KEY) ?? '[]');
    if (Array.isArray(parsed)) saved = parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    // Unreadable: rely on the in-memory record.
  }
  return [...new Set([...saved, ...unsavedSent])];
}

function writeSent(sent: string[]): void {
  try {
    window.sessionStorage.setItem(SENT_KEY, JSON.stringify(sent));
  } catch {
    unsavedSent = sent;
  }
}

/** Takes a report slot for this crash: false when it was already sent or the session's quota is used. */
function claimReportSlot(report: ClientErrorReport): boolean {
  const fingerprint = `${report.name}\n${report.message}\n${report.path}`.slice(0, MAX_FINGERPRINT_LENGTH);
  const sent = readSent();
  if (sent.length >= MAX_REPORTS_PER_SESSION || sent.includes(fingerprint)) return false;
  writeSent([...sent, fingerprint]);
  return true;
}

function reportHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  const { token, user, isSuperAdmin, selectedWorkspaceId } = useUserStore.getState();
  if (token) headers.Authorization = `Bearer ${token}`;
  // The workspace api.ts would send: a super admin's selected one, everyone
  // else's own. Never waits for the super admin workspace bootstrap.
  const workspaceId = isSuperAdmin ? selectedWorkspaceId : user?.workspace_id;
  if (workspaceId) headers['X-Workspace-Id'] = String(workspaceId);
  return headers;
}

/** Sends one crash report. Never throws, never waits, never tells the user. */
export function reportClientError(
  error: unknown,
  { kind, path = window.location.pathname }: { kind: ClientErrorKind; path?: string },
): void {
  try {
    const report = buildClientErrorReport(error, { kind, path });
    if (!claimReportSlot(report)) return;
    fetch(`${import.meta.env.VITE_API_URL}/client-errors`, {
      method: 'POST',
      headers: reportHeaders(),
      body: JSON.stringify(report),
      keepalive: true,
    }).catch(() => {
      // Reporting is best effort.
    });
  } catch {
    // A crash report must never cause a second crash.
  }
}
